import { Box, LinkBox, LinkOverlay, Text, VStack } from '@chakra-ui/react';
import type { VolumeButtonProps } from '@time-line/components/common';
import { MotionBox, VolumeButton } from '@time-line/components/common';
import { useSanityProduct } from 'hooks/product/useSanityProduct';
import { useImage } from 'lib/sanity';
import NextImage from 'next/image';
import NextLink from 'next/link';
import type { SocialMediaCarousel as SocialMediaCarouselProps } from 'types/cms/blocks';

import { ANIMATION_DURATION } from './constants';

type SocialMediaCarouselItemProps =
  SocialMediaCarouselProps['items'][number] & {
    volumeButtonProps: VolumeButtonProps;
    scaleFactor: number;
  };

const Gradient = ({ scaleFactor }: { scaleFactor: number }) => (
  <Box
    position="absolute"
    top="0"
    left="0"
    width="100%"
    height="100%"
    bg="linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.35) 100%)"
    transform={`scaleX(${scaleFactor})`}
    transformOrigin="bottom"
    zIndex={0}
    borderBottomRadius="base"
    overflow="hidden"
  />
);

export function SocialMediaCarouselOverlay({
  product,
  volumeButtonProps,
  scaleFactor,
}: SocialMediaCarouselItemProps) {
  const sanityProduct = useSanityProduct(product?.slug);
  const imageProps = useImage(sanityProduct?.images[0] ?? null, {
    ratio: 4 / 5,
  });

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        transition: {
          duration: 0.1,
          delay: ANIMATION_DURATION,
        },
      }}
      exit={{
        opacity: 0,
        transition: {
          duration: 0.1,
          delay: 0,
        },
      }}
    >
      <VolumeButton boxSize="32px" position="absolute" {...volumeButtonProps} />
      {sanityProduct && imageProps && (
        <LinkBox
          as={VStack}
          position="absolute"
          bottom={0}
          width="100%"
          spacing="3xs"
          py="sm"
        >
          <Box borderRadius="3px" overflow="hidden" width="40px" zIndex={1}>
            <NextImage {...imageProps} sizes="40px" />
          </Box>
          <LinkOverlay as={NextLink} href={sanityProduct.href} zIndex={1}>
            <Text color="gray.50" size="overline">
              {sanityProduct.title}
            </Text>
          </LinkOverlay>
          <Gradient scaleFactor={scaleFactor} />
        </LinkBox>
      )}
    </MotionBox>
  );
}
